import { Box } from '@mui/material';
import { Plus } from '@styled-icons/bootstrap';
import Filter from 'Components/Filter/Filter';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import usePageTitle from 'hooks/usePageTitle';
import { EActivitiesTableColumnKeys } from 'FeatureModules/Activity/Components/ActivitiesTable/activitiesHeaderColumns';
import ActivitiesTable from 'FeatureModules/Activity/Components/ActivitiesTable/ActivitiesTable';
import PartnerSkeleton from 'FeatureModules/Partner/Components/Skeleton/PartnerSkeleton';
import { CardIllustration, EmptyStateCard, InfoTooltip, ThemeButton } from 'DesignSystem';
import useActivities from 'FeatureModules/Activity/hooks/useActivities';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import ActionContainer from 'Components/ActionContainer';
import EmptySearchResults from 'Components/EmptySearchResults';
import ErrorState from 'Components/ErrorState';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

const Activities: FC = () => {
  usePageTitle('Activities');
  const {
    activities,
    loading,
    error,
    filters,
    searchTerm,
    hasNextPage,
    handlePageEndReached,
    handleSearchTermChange,
    handleCreateActivity,
    handleEditActivity,
    handleFilterChange,
    handleFilterReset,
  } = useActivities();
  const navigate = useNavigate();

  if (error) return <ErrorState />;

  if (!activities.length && !searchTerm && !filters.some(filter => !!filter.value) && !loading) {
    return (
      <PageContainer>
        <PageTitle
          title="Activities"
          TitleAdornment={
            <Box ml={1}>
              <InfoTooltip
                size={EAvailableSizes.LARGE}
                text="Manage the activities of your own company, or view and manage all activities from different partners in your supply chains."
              />
            </Box>
          }
        />

        <Box mt={4}>
          <EmptyStateCard
            data-testid="activities-getting-started-card"
            data-testid-button="activities-getting-started-card-button"
            title="Add your activities to map your supply chain and promote transparency"
            variant={EItemTypes.LOCATION}
            buttonText="Add activity"
            background={EBackgroundTypes.WORLD}
            size={EAvailableSizes.LARGE}
            renderIllustration={
              <CardIllustration
                image={EIllustrationTypes.LOCATIONS}
                width={300}
                right={25}
                top={0}
              />
            }
            onCreateItemClick={handleCreateActivity}
          />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle
        title="Activities"
        TitleAdornment={
          <Box ml={1}>
            <InfoTooltip
              size={EAvailableSizes.LARGE}
              text="Manage the activities of your own company, or view and manage all activities from different partners in your supply chains."
            />
          </Box>
        }
      />
      <Box mb={3} />

      <Filter
        filters={filters}
        onFilterChange={handleFilterChange}
        onFilterReset={handleFilterReset}
      />

      <ActionContainer>
        <SearchToolbar
          setSearchTerm={handleSearchTermChange}
          searchTerm={searchTerm || ''}
          className="search-input"
          data-testid="activity-search-input"
          placeholder="Search activity"
          actions={
            <Box ml={1}>
              <ThemeButton
                size="medium"
                color="PRIMARY"
                onClick={handleCreateActivity}
                startIcon={<Plus size={20} color="white" />}
                data-testid="create-activity-btn"
              >
                Add new activity
              </ThemeButton>
            </Box>
          }
        />
      </ActionContainer>

      {loading && <Loader />}

      {activities.length > 0 && !loading && (
        <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
          <ActivitiesTable
            columns={[
              EActivitiesTableColumnKeys.TITLE,
              EActivitiesTableColumnKeys.PARTNER,
              EActivitiesTableColumnKeys.MODIFIED_TIMESTAMP,
              EActivitiesTableColumnKeys.ACTIONS,
            ]}
            activities={activities}
            onSelect={({ id }) => navigate(`/activities/${id}`)}
            onEdit={handleEditActivity}
          />
        </InfiniteScrollWrapper>
      )}

      {!activities.length && !loading && (
        <EmptySearchResults
          message="No search results found"
          action={{
            label: 'Add new activity',
            onClick: handleCreateActivity,
          }}
          skeletonComponent={<PartnerSkeleton title={searchTerm || ''} />}
        />
      )}
    </PageContainer>
  );
};

export default Activities;
